import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../Navbar";
import Footer from "../Footer";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";

// Commercial Mode gate (V2): children render only when the account holds ACTIVE
// commercial access (server-authoritative `commercial_access` on /auth/me).
// Otherwise: request-access card (self-serve → admin approval), pending state,
// or suspended state. No commercial API is ever called without access.
export default function CommercialGate({ children }) {
  const { user, loading, refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center pt-28">
          <Loader2 className="w-6 h-6 animate-spin text-[#FF3B45]" aria-label="Loading" />
        </main>
        <Footer />
      </div>
    );
  }

  const state = user?.commercial_access || "none";
  if (state === "active") return children;

  const request = async () => {
    setBusy(true);
    try {
      await api.commercialRequestAccess({});
      toast.success("Request sent — our team will review it shortly.");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Could not send the request.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="commercial-gate">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-5 pt-28 pb-16">
        <div className="card-dark max-w-md w-full p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-2xl bg-[#E50914]/15 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-[#FF3B45]" />
          </div>
          <h1 className="font-display font-bold text-xl mt-5">
            {state === "requested" ? "Request under review" : state === "suspended" ? "Commercial access suspended" : "Commercial Mode"}
          </h1>
          <p className="text-sm text-zinc-400 leading-relaxed mt-3">
            {state === "requested"
              ? "Your Commercial Mode request is being reviewed by our team. You'll be notified as soon as it's approved."
              : state === "suspended"
              ? "Your Commercial Mode access is suspended. Please contact support if you believe this is a mistake."
              : "Commercial Mode is for property operators and businesses managing parking at scale. Request access and our team will enable it on your account."}
          </p>
          {state === "none" && (
            <button onClick={request} disabled={busy} data-testid="commercial-request-access"
                    className="btn-primary w-full mt-6 text-sm disabled:opacity-50">
              {busy ? "Sending…" : "Request Commercial Access"}
            </button>
          )}
          <Link to="/support" className="block mt-4 text-xs text-zinc-500 hover:text-zinc-300 transition-colors">
            Questions? Contact support
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
